import * as React from 'react';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  IconButton,
  ListItem,
  ListSubheader,
  SxProps,
  Tooltip,
  styled,
  alpha,
  useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { isSidebarOpenSelector } from '../../state';
import routes, { RouteConfig } from '../../config/routes';

interface Item {
  route: RouteConfig;
  icon: JSX.Element;
  tranlationKey: string;
  children?: Item[];
  noLink?: boolean;
}

const ItemBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
});

const iconStyle: SxProps = {
  mr: 1,
};

const collapsedIconSx: SxProps = {
  minWidth: 0,
  mr: 0,
  justifyContent: 'center',
};
interface NestedListProps {
  data: Item[];
  deep?: number;
  subheader?: string;
}

const NestedList = ({ data, deep, subheader }: NestedListProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const [open, setOpen] = React.useState<{ [key: string]: boolean }>({});
  const isSidebarOpen = useSelector(isSidebarOpenSelector);
  const isCollapsed = !isSidebarOpen;

  const calcDeep = () => {
    if (isSidebarOpen) return deep ? deep + 6 : 6;
    return 2;
  };

  const getFatherBgColor = () => {
    if (theme.palette.mode === 'light') return theme.palette.primary.light;
    return alpha(theme.palette.common.white, 0.06);
  };

  const getBgColor = () => {
    if (theme.palette.mode === 'light') return grey[50];
    return alpha(theme.palette.common.white, 0.03);
  };

  const currentRouteGroupKey = React.useMemo(() => {
    const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
    const currentRoute = Object.values(routes.protectedRoutes).find(
      route => route.path === normalizedPathname
    );

    return currentRoute?.menuGroupKey;
  }, [location.pathname]);

  const isCurrentPath = (route: RouteConfig) =>
    location.pathname === route.path ||
    (route.menuGroupKey !== undefined && route.menuGroupKey === currentRouteGroupKey);

  const isOpen = (path: string) => open[path] === true;

  const handleClick = (key: string) => {
    const newState = { ...open };
    newState[key] = !newState[key];
    setOpen(newState);
  };

  return (
    <List sx={{ py: 0.5 }}>
      {subheader && (
        <ListSubheader
          disableSticky
          sx={{
            lineHeight: 1,
            py: 0.75,
            px: 1,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
            color: 'text.secondary',
            backgroundColor: 'transparent',
          }}
        >
          {isSidebarOpen ? subheader : null}
        </ListSubheader>
      )}
      {data.map(i => (
        <React.Fragment key={i.route.path}>
          <Tooltip title={isSidebarOpen ? '' : t(i.tranlationKey).toString()} placement="right">
            {i.noLink ? (
              <ItemBox>
                <ListItemButton
                  sx={{
                    pl: isSidebarOpen ? deep : undefined,
                    px: isSidebarOpen ? undefined : 1,
                    justifyContent: isSidebarOpen ? 'flex-start' : 'center',
                    width: '100%',
                    minHeight: 46,
                    backgroundColor: isOpen(i.route.path) ? getFatherBgColor() : undefined,
                  }}
                  onClick={() => handleClick(i.route.path)}
                >
                  <ListItemIcon sx={isCollapsed ? collapsedIconSx : undefined}>
                    {i.icon}
                  </ListItemIcon>
                  {isSidebarOpen && <ListItemText primary={t(i.tranlationKey)} />}
                  {isSidebarOpen &&
                    (open[i.route.path] === true ? (
                      <ExpandLess color="action" sx={iconStyle} />
                    ) : (
                      <ExpandMore color="action" sx={iconStyle} />
                    ))}
                </ListItemButton>
              </ItemBox>
            ) : (
              <ListItem
                sx={{ px: 0 }}
                secondaryAction={
                  i.children &&
                  isSidebarOpen && (
                    <IconButton onClick={() => handleClick(i.route.path)}>
                      {open[i.route.path] === true ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  )
                }
              >
                <ListItemButton
                  component={NavLink}
                  to={i.route.path}
                  selected={isCurrentPath(i.route)}
                  sx={{
                    pl: isSidebarOpen ? deep : undefined,
                    px: isSidebarOpen ? undefined : 1,
                    justifyContent: isSidebarOpen ? 'flex-start' : 'center',
                    minHeight: 46,
                  }}
                >
                  <ListItemIcon sx={isCollapsed ? collapsedIconSx : undefined}>
                    {i.icon}
                  </ListItemIcon>
                  {isSidebarOpen && <ListItemText primary={t(i.tranlationKey)} />}
                </ListItemButton>
              </ListItem>
            )}
          </Tooltip>
          {i.children && (
            <Collapse
              in={open[i.route.path]}
              timeout="auto"
              unmountOnExit
              sx={{ backgroundColor: getBgColor() }}
            >
              <NestedList data={i.children} deep={calcDeep()} />
            </Collapse>
          )}
        </React.Fragment>
      ))}
    </List>
  );
};

export default NestedList;
